import {
	NO_SAMPLE,
	SAMPLE_NO_SHIPPING,
	SAMPLE_OPTIONS as SHARED_SAMPLE_OPTIONS,
} from '@codezo/offer-extras';
import {
	type OfferFeasibilityVerdict,
	InvoiceRecipientType,
	type OrderInvoiceRecipientInput,
	OrderQuoteOfferStatus,
	OrderSampleChoice,
	OrderShippingMethod,
	useAcceptOrderQuoteOffersMutation,
	useAddOrderQuoteItemsMutation,
	useCloseOrderQuoteMutation,
	useCreateOrderQuoteMutation,
	useGetAllOrganizationUserQuery,
	useGetOrganizationSettingQuery,
	usePreviewAcceptOffersQuery,
	useUpdateOrderQuoteMutation,
} from '@codezo/graphql';
import {
	AlertTriangle,
	ArrowLeft,
	ArrowRight,
	Box,
	Check,
	CheckCircle2,
	Clock,
	FilePlus2,
	Layers,
	Mail,
	Plus,
	SkipForward,
	User,
	X,
} from 'lucide-react';
import { RadioMark } from '@codezo/quote-detail';
import { formatArrival } from '../v2/format';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CurrencyText } from '../../../../components/CurrencyText';
import { TermsModal } from './TermsModal';
import { SHIPPING_METHOD_LABEL } from '../../../../lib/shipping';
import {
	EMPTY_ADDRESS,
	validateAddress,
	type AddressData,
	type AddressErrors,
} from '../../../../components/ui/address-form';
import {
	AddressPicker,
	sameAddress,
	toAddressData,
} from '../../../../components/ui/address-picker';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Input } from '../../../../components/ui/input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../../../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import {
	ProductImage,
	primaryProductImage,
	type SnapshotProductImage,
} from '../../../../components/ui/product-image';
import { useQuoteClientView, useQuoteCurrency } from '../context';
import { cn } from '../../../../lib/utils';

/* ── Types ── */

interface SampleInfo {
	totalPrice: number | null;
	minProductionDays: number | null;
	maxProductionDays: number | null;
	deliveryDaysMin: number | null;
	deliveryDaysMax: number | null;
	/** One total per choice, NO_SAMPLE at 0. `totalPrice` above is the
	 * WITH_SHIPPING figure, so quoting it beside three options that charge
	 * different amounts reads as the price changing under the client. */
	pricesByChoice: Array<{ sampleChoice: OrderSampleChoice; totalPrice: number }>;
}

interface PricingOption {
	pricingId: string;
	quantity: number;
	unitPrice: number | null;
	totalPrice: number | null;
	minProductionDays: number | null;
	maxProductionDays: number | null;
	deliveries: Array<{
		id: string;
		shippingMethod: OrderShippingMethod;
		totalPrice: number | null;
		deliveryDaysMin: number;
		deliveryDaysMax: number;
		/** True for a split: part of the tier one way, the rest another. */
		isSplit?: boolean;
		/** The legs, in sequence order. Empty on a plain option. */
		parts?: Array<{
			sequence: number;
			shippingMethod: OrderShippingMethod;
			quantity: number;
			/** This leg's own arrival — the review step states one per leg. */
			projectedDelivery?: {
				ifAcceptedNow?: { earliest: string; latest: string } | null;
			} | null;
		}>;
		projectedDelivery?: {
			ifAcceptedNow?: {
				earliest: string;
				latest: string;
				verdict: OfferFeasibilityVerdict;
			} | null;
			ifAcceptedAtDeadline?: {
				earliest: string;
				latest: string;
				verdict: OfferFeasibilityVerdict;
			} | null;
		} | null;
	}>;
}

interface OfferForAccept {
	offerId: string;
	orderQuoteItemId: string;
	batchName: string;
	productName: string;
	productImage?: SnapshotProductImage | null;
	pricingOptions: PricingOption[];
	sample?: SampleInfo | null;
	/** True when this offer's acceptance window has already passed — accepting
	 * routes to admin confirmation instead of creating an order immediately. */
	acceptDeadlinePassed?: boolean;
}

interface QuoteItem {
	id: string;
	productId: string;
	productName?: string | null;
	productSku?: string | null;
	/* Frozen snapshot of the catalogue images: url + isPrimary only, and the url
	   is signed with a one-hour expiry. Render it; never persist it or key off it. */
	productImages?: Array<{ url?: string | null; isPrimary?: boolean | null }> | null;
	quantities: Array<{ id: string; quantity: number }>;
	deadline?: string | null;
}

interface OfferBatchSummary {
	id: string;
	offers: Array<{
		id: string;
		orderQuoteItemId: string;
		status: string;
	}>;
}

/** The quote's delivery address, as `getClientOrderQuote` returns it. */
export interface QuoteDeliveryAddress {
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	stateCode?: string | null;
	postalCode?: string | null;
	countryCode?: string | null;
	googlePlaceId?: string | null;
	latitude?: number | null;
	longitude?: number | null;
}

interface QuoteContext {
	quoteId: string;
	quoteName: string;
	quoteItems: QuoteItem[];
	allBatches: OfferBatchSummary[];
	/** Where the quote currently ships to — the accept flow confirms or replaces it. */
	deliveryAddress?: QuoteDeliveryAddress | null;
	organizationId?: string | null;
}

/** A committed combination from a downloaded quote PDF (the 'master' selection),
 * used to pre-select offers/tiers/deliveries when the accept flow opens. */
interface MasterSelection {
	offerId: string;
	pricingId: string;
	deliveryId?: string;
	sampleChoice?: OrderSampleChoice;
}

interface AcceptOffersModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	offers: OfferForAccept[];
	quoteContext: QuoteContext;
	/** The combination to accept — normally what the offer card has selected,
	 * falling back to the one a quote PDF was downloaded for. */
	masterSelections?: MasterSelection[];
	onSuccess: () => void;
}

/**
 * What one delivery option is called in the review step.
 *
 * A split is named by its legs — "20 Express + 80 Standard" — not by its own
 * shippingMethod, which is DERIVED from the first leg and would call the whole
 * thing Express. Its day range is the span from the first leg out to the last
 * leg in, so it is stated as an arrival window rather than as a transit time
 * nobody waits.
 */
function describeDelivery(delivery: {
	shippingMethod: OrderShippingMethod;
	deliveryDaysMin: number;
	deliveryDaysMax: number;
	isSplit?: boolean;
	parts?: Array<{ sequence: number; shippingMethod: OrderShippingMethod; quantity: number }>;
}): string {
	if (delivery.isSplit && delivery.parts?.length) {
		return [...delivery.parts]
			.sort((a, b) => a.sequence - b.sequence)
			.map((p) => `${p.quantity.toLocaleString()} ${SHIPPING_METHOD_LABEL[p.shippingMethod]}`)
			.join(' + ');
	}
	return `${SHIPPING_METHOD_LABEL[delivery.shippingMethod]} — ${delivery.deliveryDaysMin}–${delivery.deliveryDaysMax} days`;
}

/**
 * The forward wizard, plus the two screens acceptance lands on.
 *
 * Sample and Delivery restate choices already made on the offer card rather than
 * asking something new. That is deliberate: this is the screen where money is
 * committed, and a client should see every decision it is about to act on — with
 * the option to change one — instead of taking it on trust from a page behind a
 * modal. They arrive pre-filled from the card, so confirming is one click.
 *
 * `remaining` and `pending-review` are outcomes, not steps: they replace the
 * wizard once the mutation has run, which is why they are absent from
 * WIZARD_STEPS and from the step indicator.
 */
type Step = 'sample' | 'delivery' | 'details' | 'review' | 'remaining' | 'pending-review';

interface Selection {
	offerId: string;
	pricingId: string;
	quantity: number;
	unitPrice: number | null;
	totalPrice: number | null;
	deliveryId?: string;
	deliveryLabel?: string;
	deliveryPrice?: number | null;
	sampleChoice: OrderSampleChoice;
}

/* ── Checkout payment section (net / VAT / gross, from previewAcceptOffers) ── */

function AcceptSectionRows({
	title,
	section,
	totalLabel,
	emphasizeTotal,
}: {
	title?: string;
	section: {
		netAmount: number;
		vatRate: number;
		vatAmount: number;
		grossAmount: number;
	};
	totalLabel: string;
	emphasizeTotal?: boolean;
}) {
	return (
		<div>
			{title && (
				<div className="mb-1.5 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
					{title}
				</div>
			)}
			<div className="flex items-center justify-between py-1">
				<span className="text-text-3">Subtotal</span>
				<CurrencyText amount={section.netAmount} className="text-text-2" />
			</div>
			<div className="flex items-center justify-between py-1">
				<span className="text-text-3">
					VAT ({section.vatRate}%)
					{section.vatRate === 0 && (
						<span className="ml-1 text-2xs text-text-4">reverse charge / export</span>
					)}
				</span>
				<CurrencyText amount={section.vatAmount} className="text-text-2" />
			</div>
			<div className="mt-1 flex items-center justify-between border-t border-line-1/50 pt-1.5">
				<span
					className={cn(
						'font-medium text-text-1',
						emphasizeTotal ? 'text-sm' : 'text-sm'
					)}
				>
					{totalLabel}
				</span>
				<CurrencyText
					amount={section.grossAmount}
					className={cn(
						'font-medium text-text-1',
						emphasizeTotal ? 'text-base' : 'text-sm'
					)}
				/>
			</div>
		</div>
	);
}

/* ── Steps ── */

const WIZARD_STEPS: { key: Step; label: string }[] = [
	{ key: 'sample', label: 'Sample' },
	{ key: 'delivery', label: 'Delivery' },
	{ key: 'details', label: 'Details' },
	{ key: 'review', label: 'Review' },
];

function StepIndicator({ current }: { current: Step }) {
	const currentIdx = WIZARD_STEPS.findIndex((s) => s.key === current);
	return (
		<div className="flex items-center gap-1.5 pt-1">
			{WIZARD_STEPS.map((step, i) => {
				const isDone = i < currentIdx;
				const isActive = i === currentIdx;
				return (
					<div key={step.key} className="flex items-center gap-1.5">
						{i > 0 && <div className="h-px w-3 bg-line-2" />}
						<div
							className={cn(
								'flex h-5 items-center gap-1 rounded-full px-2 text-2xs font-medium transition-colors',
								isActive
									? 'bg-accent text-accent-fg'
									: isDone
										? 'bg-accent-soft text-accent'
										: 'bg-surface-fill text-text-4'
							)}
						>
							{isDone ? <Check size={10} /> : <span>{i + 1}</span>}
							<span>{step.label}</span>
						</div>
					</div>
				);
			})}
		</div>
	);
}

/* ── Sample options ── */

/**
 * The same three options the offer card shows, taken from the shared package so
 * the client can't meet one vocabulary on the card and another at checkout.
 *
 * Only the disabled case is added here: the card lists sample-bearing products
 * only, whereas this step walks every product being accepted, so it has to say
 * why two of the three are closed on a product with no sample.
 */
/**
 * The three sample choices, as this modal draws them.
 *
 * `shortLabel` because the tiles sit three across a product row here, where the
 * shared section gives each a full-width card: "Sample with Physical Delivery"
 * is the right label there and wraps to three lines at a third of the width.
 * The shared `description` is dropped for the same reason — it restates the
 * label almost word for word ("Receive the physical sample shipped to your
 * address"), so at this size it costs a line and says nothing new.
 */
const SAMPLE_OPTIONS = SHARED_SAMPLE_OPTIONS.map((opt) => ({
	...opt,
	shortLabel:
		opt.value === NO_SAMPLE
			? 'No sample'
			: opt.value === SAMPLE_NO_SHIPPING
				? 'Video / images'
				: 'Physical delivery',
	requiresSample: opt.value !== NO_SAMPLE,
	disabledDescription:
		opt.value === NO_SAMPLE
			? opt.description
			: 'Not available — this offer has no sample configured.',
}));

/* ── Animated height wrapper ── */

function AnimatedHeight({ step, children }: { step: string; children: React.ReactNode }) {
	const contentRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number | 'auto'>('auto');

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		// Measure after render
		const frame = requestAnimationFrame(() => {
			setHeight(el.scrollHeight);
		});
		return () => cancelAnimationFrame(frame);
	}, [step]);

	// Also observe resize for dynamic content
	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		const observer = new ResizeObserver(() => {
			setHeight(el.scrollHeight);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			className="overflow-hidden transition-[height] duration-200 ease-out"
			style={{ height: height === 'auto' ? 'auto' : height }}
		>
			<div ref={contentRef} className="p-4">
				{children}
			</div>
		</div>
	);
}

/* ── Invoice recipients ── */

interface LocalRecipient {
	key: string;
	type: InvoiceRecipientType;
	userId?: string;
	email?: string;
	name?: string;
}

const RECIPIENT_EMAIL_RE = /^\S+@\S+\.\S+$/;

let recipientKeyCounter = 0;
const nextRecipientKey = () => `r_${recipientKeyCounter++}`;

const toRecipientInput = (r: LocalRecipient): OrderInvoiceRecipientInput =>
	r.type === InvoiceRecipientType.User
		? { type: InvoiceRecipientType.User, userId: r.userId, name: r.name }
		: { type: InvoiceRecipientType.Email, email: r.email, name: r.name };

/* ════════════════════════ MODAL ════════════════════════ */

export function AcceptOffersModal({
	open,
	onOpenChange,
	offers,
	quoteContext,
	masterSelections,
	onSuccess,
}: AcceptOffersModalProps) {
	// Flattened for the places that only ask "is this offer covered, and by what".
	const [step, setStep] = useState<Step>('sample');
	const [selectedOfferIds, setSelectedOfferIds] = useState<Set<string>>(new Set());
	const [selections, setSelections] = useState<Map<string, Selection>>(new Map());
	const [acceptOffers, { loading }] = useAcceptOrderQuoteOffersMutation();
	const [closeQuote, { loading: closingQuote }] = useCloseOrderQuoteMutation();
	const [createQuote, { loading: creatingQuote }] = useCreateOrderQuoteMutation();
	const [addItems, { loading: addingItems }] = useAddOrderQuoteItemsMutation();
	const [remainingAction, setRemainingAction] = useState<'new-quote' | 'skip'>('skip');
	const [acceptedOrderId, setAcceptedOrderId] = useState<string | null>(null);
	const [pendingReviewMessage, setPendingReviewMessage] = useState<string | null>(null);
	const [remainingItems, setRemainingItems] = useState<QuoteItem[]>([]);
	const remainingLoading = closingQuote || creatingQuote || addingItems;

	// Org users for invoice recipient. Prefer the quote's own organization so a
	// guest-link visitor — who has a valid session but no `user` object — still
	// gets the org's default invoice recipients.
	const { onOpenOrder, fallbackOrganizationId, currentOrganizationUser } = useQuoteClientView();
	const organizationId = quoteContext.organizationId || fallbackOrganizationId || '';

	/**
	 * Land on the new order. Where that is belongs to the app: a client goes to
	 * the platform route, a guest to their own shell (which also records the
	 * order so a reload can find it again).
	 */
	const goToOrder = (orderId: string) => {
		onOpenOrder?.(orderId, { autoOpenPayment: true });
	};

	// Checkout state
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [termsModalOpen, setTermsModalOpen] = useState(false);
	const [showCheckoutErrors, setShowCheckoutErrors] = useState(false);

	// Optional PO number — stored on the order and defaulted onto both invoices.
	const [poNumber, setPoNumber] = useState('');

	/* ── Delivery address ──
	 *
	 * The picker owns which saved address is selected — it matches on the values,
	 * because what gets stored is a copy either way. All this holds is the answer,
	 * which is written onto the quote just before the accept goes out: the order
	 * takes its address from the quote at creation.
	 */
	const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);
	const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
	const [addressCountryRules, setAddressCountryRules] = useState({
		requireState: false,
		stateLabel: 'State',
	});
	const [updateQuote, { loading: savingAddress }] = useUpdateOrderQuoteMutation();

	// Invoice recipients — multiple internal users and/or external emails.
	const [recipients, setRecipients] = useState<LocalRecipient[]>([]);
	const [seededOrg, setSeededOrg] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [recipientQuery, setRecipientQuery] = useState('');

	const { data: orgUsersData } = useGetAllOrganizationUserQuery({
		variables: {
			input: {
				organizationId,
				pagination: { limit: 100, currentPage: 1 },
			},
		},
		skip: !organizationId || !open,
		fetchPolicy: 'no-cache',
	});
	const orgUsers = orgUsersData?.getAllOrganizationUser?.data ?? [];

	// Org-level default invoice recipients — seed the list on open.
	const { data: orgSettingData } = useGetOrganizationSettingQuery({
		variables: { organizationId },
		skip: !organizationId || !open,
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		if (!open || seededOrg || !orgSettingData) return;
		const defaults = orgSettingData.getOrganizationSetting?.invoiceRecipients ?? [];
		if (defaults.length > 0) {
			setRecipients(
				defaults.map((r) => ({
					key: nextRecipientKey(),
					type: r.type,
					userId: r.userId ?? undefined,
					email: r.email ?? undefined,
					name: r.name ?? undefined,
				}))
			);
		} else if (currentOrganizationUser?.id) {
			// Fallback: whoever is signed in, when the app offers one.
			const first = currentOrganizationUser.firstName ?? '';
			const last = currentOrganizationUser.lastName ?? '';
			setRecipients([
				{
					key: nextRecipientKey(),
					type: InvoiceRecipientType.User,
					userId: currentOrganizationUser.id,
					name: `${first} ${last}`.trim() || undefined,
				},
			]);
		}
		setSeededOrg(true);
	}, [open, seededOrg, orgSettingData, currentOrganizationUser]);

	/* Seeded from the quote on open: it's what was quoted, and silently
	 * redirecting the delivery at checkout is not a default anyone asked for. The
	 * picker takes it from there — a known address selects its saved row, an
	 * unmatched one opens the form pre-filled — and only a quote with no address
	 * at all leaves the picker to fall back to the primary.
	 */
	// Once per open, like the master selection above: `quoteContext` is rebuilt on
	// every parent render and the quote is refetched while this is on screen, so
	// re-running would throw away an address the client had already changed.
	const seededAddressRef = useRef(false);
	useEffect(() => {
		if (!open) {
			seededAddressRef.current = false;
			return;
		}
		if (seededAddressRef.current) return;
		seededAddressRef.current = true;
		const quoteAddress = quoteContext.deliveryAddress;
		setAddress(quoteAddress?.addressLine1 ? toAddressData(quoteAddress) : EMPTY_ADDRESS);
	}, [open, quoteContext.deliveryAddress]);

	const addressComplete = Object.keys(validateAddress(address, addressCountryRules)).length === 0;

	const reset = () => {
		setStep('sample');
		setSelectedOfferIds(new Set());
		setSelections(new Map());
		setRemainingAction('skip');
		setAcceptedOrderId(null);
		setPendingReviewMessage(null);
		setRemainingItems([]);
		setTermsAccepted(false);
		setRecipients([]);
		setSeededOrg(false);
		setAddOpen(false);
		setRecipientQuery('');
		setShowCheckoutErrors(false);
		setPoNumber('');
		setAddress(EMPTY_ADDRESS);
		setAddressErrors({});
	};

	// The combination to accept, seeded once per open. It comes from the offer
	// card — products, quantities, delivery and the plan are all chosen there, and
	// this modal only asks for samples and invoice details. It falls back to the
	// downloaded quote's 'master' selection when no card has reported yet.
	//
	// Still validated against the current offers: a pricing or delivery the admin
	// edited away is skipped, and one offer per product.
	const seededMasterRef = useRef(false);
	useEffect(() => {
		if (!open) {
			seededMasterRef.current = false;
			return;
		}
		if (seededMasterRef.current) return;

		if (!masterSelections || masterSelections.length === 0) return;

		const nextOfferIds = new Set<string>();
		const nextSelections = new Map<string, Selection>();
		const usedItemIds = new Set<string>();
		for (const ms of masterSelections) {
			const offer = offers.find((o) => o.offerId === ms.offerId);
			if (!offer || usedItemIds.has(offer.orderQuoteItemId)) continue;
			const pricing = offer.pricingOptions.find((p) => p.pricingId === ms.pricingId);
			if (!pricing) continue;
			const delivery = ms.deliveryId
				? pricing.deliveries.find((d) => d.id === ms.deliveryId)
				: undefined;
			// A sample choice that needs a sample is only valid if the offer has one.
			const sampleChoice =
				ms.sampleChoice && (offer.sample || ms.sampleChoice === OrderSampleChoice.NoSample)
					? ms.sampleChoice
					: OrderSampleChoice.NoSample;
			usedItemIds.add(offer.orderQuoteItemId);
			nextOfferIds.add(offer.offerId);
			nextSelections.set(offer.offerId, {
				offerId: offer.offerId,
				pricingId: pricing.pricingId,
				quantity: pricing.quantity,
				unitPrice: pricing.unitPrice,
				totalPrice: pricing.totalPrice,
				deliveryId: delivery?.id,
				deliveryLabel: delivery ? describeDelivery(delivery) : undefined,
				deliveryPrice: delivery?.totalPrice ?? undefined,
				sampleChoice,
			});
		}

		if (nextSelections.size > 0) {
			setSelectedOfferIds(nextOfferIds);
			setSelections(nextSelections);
		}
		seededMasterRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, masterSelections, offers]);

	const selectedOffers = offers.filter((o) => selectedOfferIds.has(o.offerId));
	const currentStepIdx = WIZARD_STEPS.findIndex((s) => s.key === step);

	// Authoritative accept preview — net / VAT / gross per section (pay now = samples,
	// pay later = production invoice). Same selection input as the accept mutation.
	const selectedCurrency = useQuoteCurrency();
	const previewSelections = Array.from(selections.values())
		.filter((s) => selectedOfferIds.has(s.offerId))
		.map((s) => ({
			offerId: s.offerId,
			pricingId: s.pricingId,
			quantity: s.quantity,
			deliveryId: s.deliveryId || undefined,
			sampleChoice: s.sampleChoice,
		}));
	const { data: previewData, loading: previewLoading } = usePreviewAcceptOffersQuery({
		variables: {
			input: {
				orderQuoteId: quoteContext.quoteId,
				inCurrency: selectedCurrency,
				selections: previewSelections,
			},
		},
		skip: step !== 'review' || previewSelections.length === 0,
		fetchPolicy: 'no-cache',
	});
	const acceptPreview = previewData?.previewAcceptOffers ?? null;

	/* ── Selection helpers ── */

	const selectSampleChoice = (offerId: string, choice: OrderSampleChoice) => {
		setSelections((prev) => {
			const next = new Map(prev);
			const existing = next.get(offerId);
			if (existing) {
				next.set(offerId, { ...existing, sampleChoice: choice });
			}
			return next;
		});
	};

	/* ── Navigation ── */

	/**
	 * Whether this step is answered. Checked per step rather than all at the end,
	 * so a missing address is reported on the screen that asks for it instead of
	 * on the one that commits.
	 */
	const canGoNext = () => {
		if (step === 'sample') {
			return selectedOffers.every((o) => !!selections.get(o.offerId)?.sampleChoice);
		}
		if (step === 'delivery') {
			// A product with no option priced on its tier cannot be given one here,
			// so it does not hold up the step — the offer card already said so.
			return selectedOffers.every((o) => {
				const sel = selections.get(o.offerId);
				const pricing = o.pricingOptions.find((p) => p.pricingId === sel?.pricingId);
				return !pricing?.deliveries.length || !!sel?.deliveryId;
			});
		}
		if (step === 'details') {
			return addressComplete && recipients.length > 0;
		}
		return true;
	};

	/** Pick how one product ships. The modal's answer is what gets accepted, so a
	 * change here overrides what the card had selected. */
	const setDelivery = (offerId: string, deliveryId: string) => {
		setSelections((prev) => {
			const next = new Map(prev);
			const existing = next.get(offerId);
			if (!existing) return prev;
			const offer = offers.find((o) => o.offerId === offerId);
			const pricing = offer?.pricingOptions.find((p) => p.pricingId === existing.pricingId);
			const delivery = pricing?.deliveries.find((d) => d.id === deliveryId);
			if (!delivery) return prev;
			next.set(offerId, {
				...existing,
				deliveryId,
				deliveryLabel: describeDelivery(delivery),
				deliveryPrice: delivery.totalPrice ?? undefined,
			});
			return next;
		});
	};

	const goNext = () => {
		/* The address step's fields report themselves inline, and they only start
		   doing so once someone has tried to leave without them. Before that a
		   freshly opened form would be scolding about blanks nobody has had the
		   chance to fill. */
		if (!canGoNext()) {
			if (step === 'details') setShowCheckoutErrors(true);
			return;
		}
		const nextIdx = currentStepIdx + 1;
		if (nextIdx < WIZARD_STEPS.length) setStep(WIZARD_STEPS[nextIdx].key);
	};

	const goBack = () => {
		const prevIdx = currentStepIdx - 1;
		if (prevIdx >= 0) setStep(WIZARD_STEPS[prevIdx].key);
	};

	/* ── Invoice recipient add/remove ── */

	const query = recipientQuery.trim();

	const availableUsers = orgUsers.filter(
		(u) => !recipients.some((r) => r.type === InvoiceRecipientType.User && r.userId === u.id)
	);
	const matchedUsers = availableUsers.filter((u) => {
		if (!query) return true;
		const hay =
			`${u.firstName} ${u.lastName} ${u.authenticationUser?.email ?? ''}`.toLowerCase();
		return hay.includes(query.toLowerCase());
	});

	const emailDuplicate = recipients.some(
		(r) =>
			r.type === InvoiceRecipientType.Email && r.email?.toLowerCase() === query.toLowerCase()
	);
	const canAddEmail = RECIPIENT_EMAIL_RE.test(query) && !emailDuplicate;

	const addUserRecipient = (u: (typeof orgUsers)[number]) => {
		const name = `${u.firstName} ${u.lastName}`.trim() || undefined;
		setRecipients((prev) => [
			...prev,
			{ key: nextRecipientKey(), type: InvoiceRecipientType.User, userId: u.id, name },
		]);
		setRecipientQuery('');
	};

	const addEmailRecipient = () => {
		if (!canAddEmail) return;
		setRecipients((prev) => [
			...prev,
			{ key: nextRecipientKey(), type: InvoiceRecipientType.Email, email: query },
		]);
		setRecipientQuery('');
	};

	const removeRecipient = (key: string) => {
		setRecipients((prev) => prev.filter((r) => r.key !== key));
	};

	const recipientChipLabel = (r: LocalRecipient): string => {
		if (r.type === InvoiceRecipientType.User) {
			const u = orgUsers.find((ou) => ou.id === r.userId);
			const name = u ? `${u.firstName} ${u.lastName}`.trim() : r.name;
			return name || u?.authenticationUser?.email || r.name || 'User';
		}
		return r.name || r.email || 'Email';
	};

	/* ── Submit ── */

	/**
	 * Writes the picked address onto the quote. Returns false if it didn't take,
	 * so the caller can stop before accepting.
	 *
	 * A no-op when the quote already holds this address — the common path, where
	 * the client just confirms what's shown.
	 */
	const saveDeliveryAddress = async (): Promise<boolean> => {
		const current = quoteContext.deliveryAddress;
		if (current?.addressLine1 && sameAddress(current, address)) return true;

		try {
			const { data } = await updateQuote({
				variables: {
					id: quoteContext.quoteId,
					input: {
						deliveryAddress: {
							addressLine1: address.addressLine1.trim(),
							addressLine2: address.addressLine2 || undefined,
							city: address.city || undefined,
							stateCode: address.stateCode || undefined,
							postalCode: address.postalCode || undefined,
							countryCode: address.countryCode || undefined,
							googlePlaceId: address.googlePlaceId || undefined,
							latitude: address.latitude,
							longitude: address.longitude,
						},
					},
				},
			});

			// The API reports permission/validation failures as success: false
			// rather than throwing, so a thrown error isn't the only failure mode.
			if (data?.updateOrderQuote?.success) return true;
			toast.error(data?.updateOrderQuote?.message || 'Failed to save the delivery address');
			return false;
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save the delivery address');
			return false;
		}
	};

	const handleAccept = async () => {
		// Validate checkout fields
		if (!termsAccepted || recipients.length === 0 || !addressComplete) {
			setShowCheckoutErrors(true);
			setAddressErrors(validateAddress(address, addressCountryRules));
			return;
		}

		// The address goes onto the quote first: the accept mutation carries no
		// address, and the order it creates reads its delivery address from the
		// quote. A failure here stops the accept — an order that ships to the
		// wrong place is worse than one not placed yet.
		if (!(await saveDeliveryAddress())) return;

		try {
			const { data } = await acceptOffers({
				variables: {
					input: {
						orderQuoteId: quoteContext.quoteId,
						invoiceRecipients: recipients.map(toRecipientInput),
						poNumber: poNumber.trim() || undefined,
						selections: Array.from(selections.values())
							.filter((s) => selectedOfferIds.has(s.offerId))
							.map((s) => ({
								offerId: s.offerId,
								pricingId: s.pricingId,
								quantity: s.quantity,
								deliveryId: s.deliveryId || undefined,
								sampleChoice: s.sampleChoice,
							})),
					},
				},
			});

			if (data?.acceptOrderQuoteOffers?.success) {
				const message = data.acceptOrderQuoteOffers.message;

				// Post-deadline acceptance: no order was created. The acceptance was
				// captured and routed to the team for confirmation. Show a terminal
				// "sent for confirmation" state instead of routing to an order.
				if (data.acceptOrderQuoteOffers.pendingReview) {
					toast.success(message || 'Acceptance sent for confirmation');
					setPendingReviewMessage(message ?? null);
					setStep('pending-review');
					return;
				}

				toast.success(message || 'Offers accepted');
				const orderId = data.acceptOrderQuoteOffers.orderId ?? null;
				setAcceptedOrderId(orderId);

				// Determine which item IDs were just accepted
				const acceptedItemIds = new Set(
					Array.from(selections.values())
						.filter((s) => selectedOfferIds.has(s.offerId))
						.map((s) => {
							const offer = offers.find((o) => o.offerId === s.offerId);
							return offer?.orderQuoteItemId;
						})
						.filter(Boolean)
				);

				// Find items that were already accepted in other batches
				const previouslyAcceptedItemIds = new Set(
					quoteContext.allBatches.flatMap((batch) =>
						batch.offers
							.filter((o) => o.status === OrderQuoteOfferStatus.Accepted)
							.map((o) => o.orderQuoteItemId)
					)
				);

				// Remaining = quote items with no accepted offer (not just accepted, not previously accepted)
				const remaining = quoteContext.quoteItems.filter(
					(item) =>
						!acceptedItemIds.has(item.id) && !previouslyAcceptedItemIds.has(item.id)
				);

				if (remaining.length > 0) {
					setRemainingItems(remaining);
					setStep('remaining');
				} else {
					// All items covered — close the quote automatically
					try {
						await closeQuote({ variables: { id: quoteContext.quoteId } });
					} catch {
						// Non-critical — quote stays open
					}
					reset();
					onOpenChange(false);
					onSuccess();
					// First landing on the new order → auto-open the payment modal.
					if (orderId) goToOrder(orderId);
				}
			} else {
				toast.error(data?.acceptOrderQuoteOffers?.message || 'Failed to accept offers');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to accept offers');
		}
	};

	const handleRemainingAction = async () => {
		try {
			if (remainingAction === 'new-quote') {
				// Create a new quote with the remaining items
				const { data: createData } = await createQuote({
					variables: {
						input: {
							name: `${quoteContext.quoteName} (continued)`,
							organizationId: quoteContext.organizationId || undefined,
						},
					},
				});

				if (
					createData?.createOrderQuote?.success &&
					createData.createOrderQuote.referenceKey
				) {
					const newQuoteId = createData.createOrderQuote.referenceKey;
					// The new quote is empty, so nothing here can already be on it —
					// but the original quote is closed below, so a silent failure
					// would strand the remaining items with nowhere to go.
					const { data: addData } = await addItems({
						variables: {
							input: {
								orderQuoteId: newQuoteId,
								items: remainingItems.map((item) => ({
									productId: item.productId,
									quantities: item.quantities.map((q) => q.quantity),
									deadline: item.deadline || undefined,
								})),
							},
						},
					});

					if (!addData?.addOrderQuoteItems?.success) {
						toast.error(
							addData?.addOrderQuoteItems?.message ||
								'Failed to move the remaining items to the new quote'
						);
						return;
					}

					toast.success('New quote created with remaining items');
				} else {
					toast.error(
						createData?.createOrderQuote?.message || 'Failed to create new quote'
					);
					return;
				}
			}

			// Close the original quote in both cases
			await closeQuote({ variables: { id: quoteContext.quoteId } });

			reset();
			onOpenChange(false);
			onSuccess();
			// First landing on the new order → auto-open the payment modal.
			if (acceptedOrderId) goToOrder(acceptedOrderId);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to process remaining items');
		}
	};

	/* ── Render ── */

	return (
		<>
			<Dialog
				open={open}
				disablePointerDismissal
				onOpenChange={(next) => {
					// Prevent closing during the remaining step — user must make a decision
					if (!next && step === 'remaining') return;
					// Closing the pending-review state acts as "Done" — refetch the parent.
					if (!next && step === 'pending-review') {
						reset();
						onOpenChange(false);
						onSuccess();
						return;
					}
					if (!next) reset();
					onOpenChange(next);
				}}
			>
				{/* Wider than the lg it was on two steps: the sample step lays its
				    three options side by side per product, and the review step holds a
				    summary, a shipping recap and a cost breakdown. At lg the options
				    had to stack, which made a three-product quote scroll for its first
				    question. */}
				<DialogContent className="sm:max-w-2xl" showCloseButton={step !== 'remaining'}>
					<DialogHeader>
						<DialogTitle>
							{step === 'remaining'
								? 'Remaining items'
								: step === 'pending-review'
									? 'Acceptance sent for confirmation'
									: 'Accept offers'}
						</DialogTitle>
						<DialogDescription>
							{step === 'remaining' ? (
								<span className="text-sm text-text-3">
									Some products on this quote don’t have accepted offers yet.
								</span>
							) : step === 'pending-review' ? (
								<span className="text-sm text-text-3">
									The acceptance window had passed, so your team needs to confirm.
								</span>
							) : (
								<StepIndicator current={step} />
							)}
						</DialogDescription>
					</DialogHeader>

					<DialogBody className="p-0">
						<AnimatedHeight step={step}>
							{/* ── 1. Sample ── */}
							{step === 'sample' && (
								<div className="flex flex-col gap-4">
									<div className="text-sm text-text-3">
										Choose whether you’d like a sample for each product. A
										sample is approved before production starts.
									</div>
									{selectedOffers.map((offer) => {
										const sel = selections.get(offer.offerId);
										if (!sel) return null;
										const hasSample = !!offer.sample;

										return (
											<div key={offer.offerId}>
												<div className="mb-1.5 flex items-center gap-2">
													{offer.productImage && (
														<ProductImage
															image={offer.productImage}
															className="h-5 w-5 rounded-[var(--r-1)] border border-line-1"
														/>
													)}
													<span className="min-w-0 truncate text-sm font-medium text-text-1">
														{offer.productName}
													</span>
													{!hasSample && (
														<span className="shrink-0 text-xs text-text-4">
															no sample offered
														</span>
													)}
												</div>

												{/* Three across, not stacked. They are alternatives on
												    one question, and side by side the prices that decide
												    between them line up and can be read against each
												    other — which is the whole comparison. */}
												<div className="grid gap-1.5 sm:grid-cols-3">
													{SAMPLE_OPTIONS.map((opt) => {
														const isSelected =
															sel.sampleChoice === opt.value;
														const disabled =
															opt.requiresSample && !hasSample;
														const Icon = opt.icon;
														const price =
															offer.sample?.pricesByChoice.find(
																(p) => p.sampleChoice === opt.value
															)?.totalPrice ?? null;
														return (
															<button
																key={opt.value}
																type="button"
																disabled={disabled}
																title={
																	disabled
																		? opt.disabledDescription
																		: opt.description
																}
																onClick={() =>
																	selectSampleChoice(
																		offer.offerId,
																		// The shared package speaks in
																		// raw enum values so it needn't
																		// depend on the schema; they are
																		// the strings this enum is built
																		// from.
																		opt.value as OrderSampleChoice
																	)
																}
																className={cn(
																	'flex flex-col gap-1 rounded-lg border px-2.5 py-2 text-left transition-colors',
																	disabled
																		? 'cursor-not-allowed border-line-1 opacity-40'
																		: isSelected
																			? 'cursor-pointer border-accent bg-accent-soft'
																			: 'cursor-pointer border-line-1 hover:border-line-2'
																)}
															>
																<span className="flex min-w-0 items-center gap-2">
																	<Checkbox
																		checked={isSelected}
																		tabIndex={-1}
																		className="pointer-events-none shrink-0"
																		disabled={disabled}
																	/>
																	<Icon
																		size={13}
																		className="shrink-0 text-text-3"
																	/>
																	<span className="min-w-0 flex-1 truncate text-sm text-text-1">
																		{opt.shortLabel}
																	</span>
																</span>
																{/* This option's own total — the number that
																    decides between the three. */}
																<span className="pl-[26px] font-mono text-sm text-text-2">
																	{disabled ? (
																		'—'
																	) : (
																		<CurrencyText
																			amount={price ?? 0}
																		/>
																	)}
																</span>
															</button>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							)}

							{/* ── 5. Checkout ── */}
							{/* ── 2. Delivery ── */}
							{step === 'delivery' && (
								<div className="flex flex-col gap-4">
									<div className="text-sm text-text-3">
										How each product ships. Pre-filled from the offer — change
										it here if you want to.
									</div>
									{selectedOffers.map((offer) => {
										const sel = selections.get(offer.offerId);
										if (!sel) return null;
										const pricing = offer.pricingOptions.find(
											(p) => p.pricingId === sel.pricingId
										);
										const options = pricing?.deliveries ?? [];
										/* Plain methods and splits are both options on this
										   tier, but they don't fit the same shape: a method is
										   a name and a price, a split has legs to state. So the
										   methods pair off two across and a split takes the
										   full width underneath — the same arrangement the
										   offer card uses, so the two screens agree. */
										const plain = options.filter((d) => !d.isSplit);
										const splits = options.filter((d) => d.isSplit);

										const tile = (
											option: (typeof options)[number],
											full: boolean
										) => {
											const on = sel.deliveryId === option.id;
											const legs = option.isSplit
												? [...(option.parts ?? [])].sort(
														(a, b) => a.sequence - b.sequence
													)
												: [];
											return (
												<button
													key={option.id}
													type="button"
													role="radio"
													aria-checked={on}
													onClick={() =>
														setDelivery(offer.offerId, option.id)
													}
													className={cn(
														'flex cursor-pointer flex-col gap-1 rounded-lg border px-2.5 py-2 text-left transition-colors',
														full && 'col-span-full',
														on
															? 'border-accent bg-accent-soft'
															: 'border-line-1 hover:border-line-2'
													)}
												>
													<span className="flex min-w-0 items-center gap-2">
														<RadioMark on={on} />
														{option.isSplit && (
															<Layers
																size={12}
																className="shrink-0 text-[var(--accent)]"
															/>
														)}
														<span className="min-w-0 flex-1 truncate text-sm text-text-1">
															{describeDelivery(option)}
														</span>
														<CurrencyText
															amount={option.totalPrice}
															className="shrink-0 font-mono text-sm text-text-2"
														/>
													</span>
													{/* A split's legs, so the quantities it divides
													    into are visible before it is taken. No
													    arrivals — the offer card states those. */}
													{legs.length > 0 && (
														<span className="flex flex-col gap-0.5 pl-[26px]">
															{legs.map((leg, i) => (
																<span
																	key={i}
																	className="text-xs text-text-4"
																>
																	<span className="font-mono text-text-3">
																		{leg.quantity.toLocaleString()}
																	</span>{' '}
																	pcs{' '}
																	{
																		SHIPPING_METHOD_LABEL[
																			leg.shippingMethod
																		]
																	}
																</span>
															))}
														</span>
													)}
												</button>
											);
										};

										return (
											<div key={offer.offerId}>
												<div className="mb-1.5 flex items-center gap-2">
													{offer.productImage && (
														<ProductImage
															image={offer.productImage}
															className="h-5 w-5 rounded-[var(--r-1)] border border-line-1"
														/>
													)}
													<span className="min-w-0 truncate text-sm font-medium text-text-1">
														{offer.productName}
													</span>
													<span className="shrink-0 font-mono text-xs text-text-4">
														{sel.quantity.toLocaleString()} pcs
													</span>
												</div>

												{options.length === 0 ? (
													<div className="rounded-lg border border-line-1 px-3 py-2.5 text-sm text-text-4">
														No shipping is priced on this quantity — we
														will be in touch to arrange it.
													</div>
												) : (
													<div className="grid gap-1.5 sm:grid-cols-2">
														{plain.map((option) => tile(option, false))}
														{splits.map((option) => tile(option, true))}
													</div>
												)}
											</div>
										);
									})}
								</div>
							)}

							{/* ── 3. Details: where it goes, and who is invoiced ── */}
							{step === 'details' && (
								<div className="flex flex-col gap-4">
									{/* Delivery address — the same picker the admin gets on a quote,
								    over the same address book. */}
									<div>
										<div className="mb-1.5 flex items-baseline justify-between">
											<span className="text-sm font-medium text-text-1">
												Delivery address
											</span>
											<span className="text-xs text-text-4">
												Where this order ships
											</span>
										</div>

										<div
											className={cn(
												showCheckoutErrors &&
													!addressComplete &&
													'rounded-lg ring-1 ring-destructive'
											)}
										>
											<AddressPicker
												organizationId={organizationId}
												value={address}
												onChange={(next) => {
													setAddress(next);
													// Clear a message the moment its field is filled
													// in, rather than making you submit again to find out.
													setAddressErrors((prev) =>
														Object.keys(prev).length > 0
															? validateAddress(
																	next,
																	addressCountryRules
																)
															: prev
													);
												}}
												errors={addressErrors}
												onCountryRulesChange={setAddressCountryRules}
												skip={!open}
												// The quote's own address wins when it has one; only a
												// quote without one falls back to the primary.
												autoSelectPrimary={
													!quoteContext.deliveryAddress?.addressLine1
												}
											/>
										</div>

										{showCheckoutErrors && !addressComplete && (
											<p className="mt-1.5 text-xs text-destructive">
												Choose or complete the delivery address.
											</p>
										)}

										<p className="mt-1.5 text-xs text-text-4">
											Saved on the quote when you accept. Manage your
											addresses in Settings.
										</p>
									</div>

									{/* Invoice recipients */}
									<div>
										<div className="mb-1.5 flex items-baseline justify-between">
											<span className="text-sm font-medium text-text-1">
												Invoice recipients
											</span>
											<span className="text-xs text-text-4">
												All receive the invoice
											</span>
										</div>

										{/* Chips + add trigger in one field-like container */}
										<div
											className={cn(
												'flex flex-wrap items-center gap-1.5 rounded-lg border p-1.5',
												showCheckoutErrors && recipients.length === 0
													? 'border-destructive'
													: 'border-line-1'
											)}
										>
											{recipients.map((r) => {
												const isUser = r.type === InvoiceRecipientType.User;
												return (
													<span
														key={r.key}
														className="inline-flex items-center gap-1.5 rounded-full bg-surface-fill py-1 pl-2 pr-1 text-sm text-text-1"
													>
														{isUser ? (
															<User
																size={11}
																className="shrink-0 text-text-4"
															/>
														) : (
															<Mail
																size={11}
																className="shrink-0 text-text-4"
															/>
														)}
														<span className="max-w-[180px] truncate">
															{recipientChipLabel(r)}
														</span>
														<button
															type="button"
															onClick={() => removeRecipient(r.key)}
															aria-label="Remove recipient"
															className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-text-4 transition-colors hover:bg-surface-input hover:text-text-1"
														>
															<X size={11} />
														</button>
													</span>
												);
											})}

											<Popover
												open={addOpen}
												onOpenChange={(next) => {
													setAddOpen(next);
													if (!next) setRecipientQuery('');
												}}
											>
												<PopoverTrigger className="inline-flex items-center gap-1 rounded-full border border-dashed border-line-2 px-2.5 py-1 text-sm text-text-3 transition-colors hover:border-line-1 hover:text-text-1">
													<Plus size={12} /> Add recipient
												</PopoverTrigger>
												<PopoverContent
													align="start"
													className="w-[280px] gap-0 p-0"
												>
													<div className="border-b border-line-1 p-2">
														<Input
															value={recipientQuery}
															onChange={(e) =>
																setRecipientQuery(e.target.value)
															}
															onKeyDown={(e) => {
																if (
																	e.key === 'Enter' &&
																	canAddEmail
																) {
																	e.preventDefault();
																	addEmailRecipient();
																}
															}}
															placeholder="Search users or type an email…"
															className="h-8 text-xs"
															autoFocus
														/>
													</div>
													<div className="scroll-thin max-h-[220px] overflow-y-auto p-1">
														{matchedUsers.map((u) => {
															const email =
																u.authenticationUser?.email;
															const name =
																`${u.firstName} ${u.lastName}`.trim() ||
																email ||
																'User';
															return (
																<button
																	key={u.id}
																	type="button"
																	onClick={() =>
																		addUserRecipient(u)
																	}
																	className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-text-2 transition-colors hover:bg-surface-hover hover:text-text-1"
																>
																	<User
																		size={12}
																		className="shrink-0 text-text-4"
																	/>
																	<span className="min-w-0 flex-1">
																		<span className="block truncate text-sm">
																			{name}
																		</span>
																		{email && (
																			<span className="block truncate text-xs text-text-4">
																				{email}
																			</span>
																		)}
																	</span>
																</button>
															);
														})}

														{canAddEmail && (
															<button
																type="button"
																onClick={addEmailRecipient}
																className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-text-2 transition-colors hover:bg-surface-hover hover:text-text-1"
															>
																<Mail
																	size={12}
																	className="shrink-0 text-text-4"
																/>
																<span className="min-w-0 flex-1 truncate text-sm">
																	Add{' '}
																	<span className="font-medium text-text-1">
																		{query}
																	</span>
																</span>
															</button>
														)}

														{matchedUsers.length === 0 &&
															!canAddEmail && (
																<div className="px-2 py-3 text-center text-sm text-text-4">
																	{query
																		? emailDuplicate
																			? 'Already added.'
																			: 'No users — type a full email to add.'
																		: 'No users left to add.'}
																</div>
															)}
													</div>
												</PopoverContent>
											</Popover>
										</div>

										{showCheckoutErrors && recipients.length === 0 && (
											<p className="mt-1.5 text-xs text-destructive">
												Add at least one invoice recipient.
											</p>
										)}

										<p className="mt-1.5 text-xs text-text-4">
											You can change recipients later from the order details
											page.
										</p>
									</div>

									{/* PO number — flows onto both the sample and production invoices */}
									<div>
										<div className="mb-1.5 flex items-baseline justify-between">
											<label
												htmlFor="accept-po-number"
												className="text-sm font-medium text-text-1"
											>
												PO number
											</label>
											<span className="text-xs text-text-4">Optional</span>
										</div>
										<Input
											id="accept-po-number"
											value={poNumber}
											onChange={(e) => setPoNumber(e.target.value)}
											placeholder="e.g. PO-12345"
										/>
										<p className="mt-1.5 text-xs text-text-4">
											Added to both the sample and production invoices. You
											can still change it at checkout.
										</p>
									</div>
								</div>
							)}

							{step === 'review' &&
								(() => {
									const hasNoSample = selectedOffers.some((o) => {
										const sel = selections.get(o.offerId);
										return sel?.sampleChoice === OrderSampleChoice.NoSample;
									});

									return (
										<div className="flex flex-col gap-4">
											<div>
												<div className="mb-2 text-xs font-medium tracking-[0.06em] text-text-4 uppercase">
													Products
												</div>
												{/* One card, hairline-separated — not a bordered box per
												    product. This is a ledger being read down, and a
												    border round every line turns three products into
												    three competing objects.
												 *
												 * No thumbnails either: they cost ~10px of row height
												 * each and identify nothing the name doesn't, on a
												 * screen the client reached by picking these products
												 * themselves. */}
												<div className="overflow-hidden rounded-lg border border-line-1">
													{selectedOffers.map((offer, i) => {
														const sel = selections.get(offer.offerId);
														if (!sel) return null;
														const sampleLabel = offer.sample
															? sel.sampleChoice ===
																OrderSampleChoice.SampleWithShipping
																? 'Physical sample'
																: sel.sampleChoice ===
																	  OrderSampleChoice.SampleNoShipping
																	? 'Video / images'
																	: 'No sample'
															: 'No sample offered';
														return (
															<div
																key={offer.offerId}
																className={cn(
																	'flex items-baseline gap-3 px-3 py-2',
																	i > 0 &&
																		'border-t border-line-1'
																)}
															>
																<span className="min-w-0 flex-1">
																	<span className="block truncate text-sm font-medium text-text-1">
																		{offer.productName}
																	</span>
																	<span className="block text-xs text-text-4">
																		{sel.quantity.toLocaleString()}{' '}
																		pcs · {sampleLabel}
																	</span>
																</span>
																<CurrencyText
																	amount={sel.totalPrice}
																	className="shrink-0 font-mono text-sm text-text-1"
																/>
															</div>
														);
													})}
												</div>
											</div>

											{/* How it ships — a line per product. A combination plan
											    used to replace this for the products it pinned, and
											    was drawn as its own shipments instead, because a
											    covered product otherwise read as shipping nowhere.
											    Plans are gone: every product carries its own
											    delivery option, a split included. */}
											<div>
												<div className="mb-2 text-xs font-medium tracking-[0.06em] text-text-4 uppercase">
													Delivery
												</div>
												<div className="overflow-hidden rounded-lg border border-line-1">
													{selectedOffers.map((offer, i) => {
														const sel = selections.get(offer.offerId);
														if (!sel) return null;
														const pricing = offer.pricingOptions.find(
															(p) => p.pricingId === sel.pricingId
														);
														const option = pricing?.deliveries.find(
															(d) => d.id === sel.deliveryId
														);
														/* Each leg's own arrival on a split, the
														   option's otherwise. This is the one screen
														   where the dates are the point — it is what
														   the client is agreeing to receive, and
														   when. */
														const arrivals = option?.isSplit
															? [...(option.parts ?? [])]
																	.sort(
																		(a, b) =>
																			a.sequence - b.sequence
																	)
																	.map((leg) => {
																		// Already reads "arrives …".
																		const at = formatArrival(
																			leg.projectedDelivery
																		);
																		return {
																			key: leg.sequence,
																			text: `${leg.quantity.toLocaleString()} pcs ${SHIPPING_METHOD_LABEL[leg.shippingMethod]}${at ? ` · ${at}` : ''}`,
																		};
																	})
															: formatArrival(
																		option?.projectedDelivery
																  )
																? [
																		{
																			key: 0,
																			text: formatArrival(
																				option?.projectedDelivery
																			) as string,
																		},
																	]
																: [];
														return (
															<div
																key={offer.offerId}
																className={cn(
																	'flex items-baseline gap-3 px-3 py-2',
																	i > 0 &&
																		'border-t border-line-1'
																)}
															>
																<span className="min-w-0 flex-1">
																	<span className="block truncate text-sm text-text-1">
																		{offer.productName}
																		{sel.deliveryLabel && (
																			<span className="text-text-3">
																				{' · '}
																				{sel.deliveryLabel}
																			</span>
																		)}
																	</span>
																	{arrivals.length > 0 ? (
																		arrivals.map((a) => (
																			<span
																				key={a.key}
																				className="block text-xs text-text-4"
																			>
																				{a.text}
																			</span>
																		))
																	) : (
																		<span className="block text-xs text-text-4">
																			{sel.deliveryLabel
																				? 'arrival not quoted yet'
																				: 'No delivery selected'}
																		</span>
																	)}
																</span>
																<CurrencyText
																	amount={
																		sel.deliveryPrice ?? null
																	}
																	className="shrink-0 font-mono text-sm text-text-1"
																/>
															</div>
														);
													})}
												</div>
											</div>

											{/* Payment breakdown (authoritative — incl. VAT) */}
											<div className="rounded-lg border border-line-1 p-3">
												{acceptPreview ? (
													<div className="flex flex-col gap-3 text-sm">
														{acceptPreview.payNow.grossAmount > 0 ? (
															<>
																<AcceptSectionRows
																	title="Due now · samples"
																	section={acceptPreview.payNow}
																	totalLabel="Due now incl. VAT"
																/>
																<AcceptSectionRows
																	title="Due later · production invoice"
																	section={acceptPreview.payLater}
																	totalLabel="Due later incl. VAT"
																/>
																<div className="mt-1 flex items-center justify-between border-t border-line-1 pt-2">
																	<span className="text-sm font-medium text-text-1">
																		Total incl. VAT
																	</span>
																	<CurrencyText
																		amount={
																			acceptPreview.total
																				.grossAmount
																		}
																		className="text-lg font-medium text-text-1"
																	/>
																</div>
															</>
														) : (
															<AcceptSectionRows
																section={acceptPreview.total}
																totalLabel="Total incl. VAT"
																emphasizeTotal
															/>
														)}
													</div>
												) : (
													<div className="py-2 text-center text-sm text-text-4">
														{previewLoading
															? 'Calculating totals…'
															: 'Totals unavailable'}
													</div>
												)}
											</div>

											{/* Late-acceptance notice — window passed, routes to confirmation */}
											{selectedOffers.some((o) => o.acceptDeadlinePassed) && (
												<div className="flex items-start gap-2.5 rounded-lg border border-status-warn/20 bg-status-warn/5 px-3 py-2.5">
													<Clock
														size={14}
														className="mt-0.5 shrink-0 text-status-warn"
													/>
													<div className="text-xs leading-relaxed text-text-2">
														The acceptance window for one or more of
														these offers has passed. Your acceptance
														will be sent to the team for confirmation
														instead of creating an order right away.
													</div>
												</div>
											)}

											{/* No-sample disclaimer */}
											{hasNoSample && (
												<div className="flex items-start gap-2.5 rounded-lg border border-status-warn/20 bg-status-warn/5 px-3 py-2.5">
													<AlertTriangle
														size={14}
														className="mt-0.5 shrink-0 text-status-warn"
													/>
													<div className="text-xs leading-relaxed text-text-2">
														You chose to skip sample validation for one
														or more products. Production will begin
														based on design specifications. We are not
														liable for discrepancies in color, size,
														materials, or print placement.
													</div>
												</div>
											)}

											{/* Terms & conditions */}
											<div className="rounded-lg border border-line-1 px-3 py-3">
												<label className="flex items-start gap-2.5 cursor-pointer">
													<Checkbox
														checked={termsAccepted}
														onCheckedChange={(checked) =>
															setTermsAccepted(!!checked)
														}
														className="mt-0.5"
													/>
													<span className="text-sm text-text-2">
														I agree to the{' '}
														<button
															type="button"
															onClick={(e) => {
																e.preventDefault();
																setTermsModalOpen(true);
															}}
															className="text-accent underline underline-offset-2 hover:text-accent-hover"
														>
															terms and conditions
														</button>
													</span>
												</label>
												{showCheckoutErrors && !termsAccepted && (
													<p className="mt-1.5 pl-6 text-xs text-destructive">
														Required to continue.
													</p>
												)}
											</div>
										</div>
									);
								})()}

							{/* ── 6. Remaining items (conditional, after accept) ── */}
							{step === 'remaining' && (
								<div className="flex flex-col gap-4">
									{/* Success banner */}
									<div className="flex items-center gap-3 rounded-lg border border-status-success/20 bg-status-success/5 px-3 py-2.5">
										<CheckCircle2
											size={16}
											className="shrink-0 text-status-success"
										/>
										<div>
											<div className="text-sm font-medium text-text-1">
												Order created
											</div>
											{acceptedOrderId && (
												<div className="text-xs text-text-3">
													Order ID: {acceptedOrderId}
												</div>
											)}
										</div>
									</div>

									{/* Remaining items list */}
									<div>
										<div className="mb-2 text-sm text-text-3">
											{remainingItems.length} product
											{remainingItems.length !== 1 ? 's' : ''} still{' '}
											{remainingItems.length !== 1 ? 'need' : 'needs'} offers:
										</div>
										<div className="flex flex-col gap-1.5">
											{remainingItems.map((item) => {
												const img = primaryProductImage(item.productImages);
												return (
													<div
														key={item.id}
														className="flex items-center gap-3 rounded-lg border border-line-1 px-3 py-2.5"
													>
														<ProductImage
															image={img}
															className="h-8 w-8 shrink-0 rounded-[var(--r-2)] border border-line-1 bg-surface-fill"
															fallback={
																<Box
																	size={12}
																	className="text-text-4"
																/>
															}
														/>
														<div className="min-w-0 flex-1">
															<div className="text-sm font-medium text-text-1">
																{item.productName ||
																	'Unknown product'}
															</div>
															<div className="text-xs text-text-4">
																{item.quantities
																	.map((q) =>
																		q.quantity.toLocaleString()
																	)
																	.join(', ')}{' '}
																pcs
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</div>

									{/* Action choices */}
									<div>
										<div className="mb-2 text-sm text-text-3">
											What would you like to do?
										</div>
										<div className="flex flex-col gap-1.5">
											<button
												type="button"
												onClick={() => setRemainingAction('new-quote')}
												className={cn(
													'flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
													remainingAction === 'new-quote'
														? 'border-accent bg-accent-soft'
														: 'border-line-1 hover:border-line-2'
												)}
											>
												<FilePlus2
													size={14}
													className="mt-0.5 shrink-0 text-text-3"
												/>
												<div>
													<div className="text-sm font-medium text-text-1">
														Create new quote
													</div>
													<div className="text-xs text-text-4">
														A new quote will be created with the
														remaining products and quantities.
													</div>
												</div>
											</button>
											<button
												type="button"
												onClick={() => setRemainingAction('skip')}
												className={cn(
													'flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
													remainingAction === 'skip'
														? 'border-accent bg-accent-soft'
														: 'border-line-1 hover:border-line-2'
												)}
											>
												<SkipForward
													size={14}
													className="mt-0.5 shrink-0 text-text-3"
												/>
												<div>
													<div className="text-sm font-medium text-text-1">
														Skip — I’m done
													</div>
													<div className="text-xs text-text-4">
														This quote will be closed. Remaining
														products will not be quoted.
													</div>
												</div>
											</button>
										</div>
									</div>
								</div>
							)}
							{/* ── 7. Pending review (conditional, after late accept) ── */}
							{step === 'pending-review' && (
								<div className="flex flex-col gap-4">
									<div className="flex items-start gap-3 rounded-lg border border-status-info/20 bg-status-info/5 px-3 py-3">
										<Clock
											size={16}
											className="mt-0.5 shrink-0 text-status-info"
										/>
										<div>
											<div className="text-sm font-medium text-text-1">
												Sent for confirmation
											</div>
											<div className="mt-0.5 text-sm leading-relaxed text-text-3">
												{pendingReviewMessage ||
													'The acceptance window for this offer had passed, so your acceptance was sent to the team for confirmation. No order has been created yet — you’ll be notified once it’s confirmed.'}
											</div>
										</div>
									</div>
								</div>
							)}
						</AnimatedHeight>
					</DialogBody>

					<DialogFooter>
						{step === 'pending-review' ? (
							<Button
								onClick={() => {
									reset();
									onOpenChange(false);
									onSuccess();
								}}
							>
								Done
							</Button>
						) : step === 'remaining' ? (
							<Button onClick={handleRemainingAction} disabled={remainingLoading}>
								{remainingLoading
									? 'Processing…'
									: remainingAction === 'new-quote'
										? 'Create new quote'
										: 'Close quote'}
							</Button>
						) : step === 'sample' ? (
							<>
								<Button variant="outline" onClick={() => onOpenChange(false)}>
									Cancel
								</Button>
								<Button onClick={goNext} disabled={!canGoNext()}>
									Next <ArrowRight size={14} data-icon="inline-end" />
								</Button>
							</>
						) : step === 'review' ? (
							<>
								<Button variant="outline" onClick={goBack}>
									<ArrowLeft size={14} data-icon="inline-start" /> Back
								</Button>
								{/* The address is saved onto the quote first, so its
								    mutation counts as part of accepting. */}
								<Button onClick={handleAccept} disabled={loading || savingAddress}>
									{savingAddress
										? 'Saving address…'
										: loading
											? 'Accepting…'
											: 'Accept offers'}
								</Button>
							</>
						) : (
							<>
								<Button variant="outline" onClick={goBack}>
									<ArrowLeft size={14} data-icon="inline-start" /> Back
								</Button>
								{/* Enabled even when the step isn't answered, on the step
								    that has inline errors: a disabled button says "no" without
								    saying which field, and there is nowhere else for the
								    reason to appear. `goNext` refuses and reveals them. */}
								<Button
									onClick={goNext}
									disabled={step !== 'details' && !canGoNext()}
								>
									Next <ArrowRight size={14} data-icon="inline-end" />
								</Button>
							</>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<TermsModal open={termsModalOpen} onOpenChange={setTermsModalOpen} />
		</>
	);
}

export type { OfferForAccept, OfferBatchSummary, PricingOption, QuoteContext, MasterSelection };
